import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'dark' | 'light';

/** Chave de persistência (§6.10). Não alterar: builds antigos leem esta mesma chave. */
export const THEME_STORAGE_KEY = 'powerful-theme';

export const DEFAULT_THEME: Theme = 'dark';

export type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

export const ThemeContext = createContext<ThemeContextValue | null>(null);

const isTheme = (value: unknown): value is Theme => value === 'dark' || value === 'light';

/**
 * Leitura de localStorage SEMPRE protegida: modo privado e navegadores com site data
 * bloqueada lançam no próprio acesso ao storage — não só na escrita. Sem valor salvo,
 * o padrão dark tem de renderizar normalmente.
 */
function readStoredTheme(): Theme {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(saved) ? saved : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Persistência é conveniência, não requisito: a sessão continua no tema escolhido.
  }
}

/**
 * O tema dark é representado pela AUSÊNCIA do atributo, não por data-theme="dark".
 * É isso que mantém o :root como padrão e o modo claro como adição não-destrutiva (§6.10):
 * `body[data-theme='light']` sobrepõe as custom properties; sem atributo, vale o :root.
 */
function applyTheme(theme: Theme): void {
  if (theme === 'light') document.body.setAttribute('data-theme', 'light');
  else document.body.removeAttribute('data-theme');
}

export type ThemeProviderProps = {
  children: ReactNode;
  /** Fixa o tema e desliga a persistência — usado pela landing, que é dark fixa (§6.10). */
  forcedTheme?: Theme;
  /** Efeito de plataforma. A T0.4a acopla aqui o `StatusBar.setStyle()` do Capacitor. */
  onThemeChange?: (theme: Theme) => void;
};

export function ThemeProvider({ children, forcedTheme, onThemeChange }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() =>
    forcedTheme ?? (typeof window === 'undefined' ? DEFAULT_THEME : readStoredTheme()),
  );

  const active = forcedTheme ?? theme;

  useEffect(() => {
    applyTheme(active);
    onThemeChange?.(active);
  }, [active, onThemeChange]);

  const setTheme = useCallback(
    (next: Theme) => {
      if (forcedTheme) return;
      setThemeState(next);
      persistTheme(next);
    },
    [forcedTheme],
  );

  const toggleTheme = useCallback(
    () => setTheme(active === 'dark' ? 'light' : 'dark'),
    [active, setTheme],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme: active, setTheme, toggleTheme }),
    [active, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// Reexport por conveniência: o teste e os consumidores importam ambos deste módulo.
// `use-theme.ts` importa apenas o contexto e o tipo, então não há ciclo em runtime.
export { useTheme } from './use-theme';
